import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import type { BranchDefaults } from "../../lib/crm-types";
import { cn } from "../ui/cn";

/**
 * "Do we already have this one?", asked while a new company is being typed.
 *
 * This is duplicate *prevention*, and it is worth more than the review queue it
 * feeds. The queue catches a mistake after it has been made, by which point
 * somebody has to unpick two half-populated records; this catches it while the
 * person still has the account in front of them and can pick the right answer
 * in one click.
 *
 * It is also the route by which a wholesaler's nine-hundredth branch gets
 * created correctly. Typing cef.co.uk turns up the group, and filing the new
 * record under it — with the group's VAT number, company number and customer
 * type already carried across — is a button rather than a thing somebody has to
 * know to do afterwards.
 *
 * Two questions, drawn differently because they are not equally good evidence.
 * **A shared domain** is one organisation, and the group offer hangs off it.
 * **A shared name** is two of the thousands of companies called Sparks, so it
 * is a quieter note that says look, not a claim that these are the same
 * account. It still earns its place: a company with no website has no domain
 * to be caught by, which is exactly the record that gets entered twice.
 *
 * Not to be confused with the name matching `identity.ts` refuses. That one
 * writes rows into a review queue unattended, where every CEF branch would flag
 * against every other one forever. This shows a person a list, once, and
 * records nothing.
 */
export function ExistingCompanies({
  domain,
  name,
  parentCompanyId,
  onAttach,
  onDetach,
}: {
  domain: string;
  /** The name field, matched against companies we already hold. */
  name: string;
  parentCompanyId: number | null;
  onAttach: (parentId: number, defaults: BranchDefaults) => void;
  onDetach: () => void;
}) {
  /*
   * Debounced, so typing is one request rather than fourteen. Longer than the
   * search box's 180ms because nobody is waiting on this — it appears beside a
   * field they are still filling in.
   */
  const [debounced, setDebounced] = useState({ domain: "", name: "" });
  useEffect(() => {
    const timer = setTimeout(
      () => setDebounced({ domain: domain.trim(), name: name.trim() }),
      350,
    );
    return () => clearTimeout(timer);
  }, [domain, name]);

  const lookup = useQuery({
    queryKey: queryKeys.crmByDomain(debounced.domain, debounced.name),
    queryFn: () => crmApi.byDomain(debounced.domain, debounced.name),
    /* A domain needs a dot to be one, a name needs three characters before it
       stops matching half the table, and either alone is a question worth
       asking. Asking before then is noise. */
    enabled:
      (debounced.domain.includes(".") && debounced.domain.length >= 4) ||
      debounced.name.length >= 3,
    staleTime: 60_000,
  });

  const defaults = useQuery({
    queryKey: ["crm", "branch-defaults", parentCompanyId],
    queryFn: () => crmApi.branchDefaults(parentCompanyId!),
    enabled: parentCompanyId !== null,
    staleTime: 60_000,
  });

  const data = lookup.data;
  const matches = data?.matches ?? [];
  const nameMatches = data?.nameMatches ?? [];

  // Already filed under a group: say so, and offer the way back out.
  if (parentCompanyId !== null) {
    return (
      <div className="rounded-md border border-brand-200 bg-brand-50 px-3 py-2 text-[11px] dark:border-brand-900 dark:bg-brand-950 sm:col-span-2">
        <span className="text-content">
          Filing as a branch of{" "}
          <strong>
            {defaults.data?.name ?? `company #${parentCompanyId}`}
          </strong>
        </span>
        <span className="text-muted">
          {" "}
          — it inherits the group's domain, VAT and company number. Give it its
          own trade account number below.
        </span>
        <button
          type="button"
          onClick={onDetach}
          className="ml-1 underline hover:text-brand-700"
        >
          Not a branch
        </button>
      </div>
    );
  }

  if (!data || (matches.length === 0 && nameMatches.length === 0)) return null;

  const group = data.suggestedParent;

  /*
   * On its own when nothing shares the domain. Quieter than the domain warning
   * — a bordered note rather than an amber panel — because "somebody else is
   * called this too" is a thing to check, and being shouted at for typing a
   * common word teaches people to ignore the panel that matters.
   */
  if (matches.length === 0) {
    return (
      <div className="rounded-md border border-line px-3 py-2 sm:col-span-2">
        <p className="text-[11px] text-muted">
          {nameMatches.length === 1
            ? "A company with a similar name already exists"
            : `${nameMatches.length} companies have a similar name`}{" "}
          — check this is not the same account entered twice.
        </p>
        <MatchList matches={nameMatches} />
      </div>
    );
  }

  return (
    <div className="rounded-md border border-sla-warning/40 bg-sla-warning/10 px-3 py-2 sm:col-span-2">
      <p className="text-[11px] font-medium text-content">
        {matches.length === 1
          ? "One company already uses this domain"
          : `${matches.length} companies already use this domain`}
        {group ? (
          <span className="font-normal text-muted">
            {" "}
            — they look like branches of {group.name}.
          </span>
        ) : (
          <span className="font-normal text-muted">
            {" "}
            — check this is not the same account entered twice.
          </span>
        )}
      </p>

      <MatchList matches={matches} className="border-b border-sla-warning/30" />

      {/* The weaker half, under the stronger one and said as an aside. Its rows
          are already de-duplicated against the domain matches server-side, so
          anything here is a company on a *different* domain that happens to be
          called something similar -- which is the case worth a second look. */}
      {nameMatches.length > 0 ? (
        <p className="mt-1 text-[11px] text-muted">
          {nameMatches.length === 1
            ? "Another company has a similar name:"
            : `${nameMatches.length} other companies have a similar name:`}{" "}
          {nameMatches.map((match, index) => (
            <span key={match.id}>
              {index > 0 ? ", " : null}
              <a
                href={`/crm/companies/${match.id}`}
                target="_blank"
                rel="noreferrer"
                className="text-content underline hover:text-brand-600"
              >
                {match.name}
              </a>
            </span>
          ))}
        </p>
      ) : null}

      {group ? (
        <button
          type="button"
          disabled={defaults.isFetching}
          onClick={async () => {
            const inherited = await crmApi.branchDefaults(group.id);
            onAttach(group.id, inherited);
          }}
          className={cn(
            "mt-1 rounded border border-line bg-surface px-2 py-1 text-[11px] font-medium",
            "text-brand-600 hover:bg-surface-2",
          )}
        >
          Add as a branch of {group.name}
        </button>
      ) : null}
    </div>
  );
}

/**
 * The rows both halves render, and the one place the link behaviour lives.
 *
 * `branchCount` is optional because only the domain matches carry one: a name
 * match is not being offered as a group, so how many branches it has is not
 * what somebody needs to see to tell it apart from what they are typing.
 */
function MatchList({
  matches,
  className,
}: {
  matches: {
    id: number;
    name: string;
    city: string | null;
    tradeAccountNumber: string | null;
    ownerName: string | null;
    branchCount?: number;
  }[];
  className?: string;
}) {
  return (
    <ul className={cn("mt-1 max-h-24 overflow-y-auto pb-1", className)}>
      {matches.slice(0, 8).map((match) => (
        <li key={match.id} className="flex items-baseline gap-2 py-0.5">
          {/*
            Opens in a new tab deliberately: the person is mid-way through a
            form, and navigating away from it would lose what they have typed.
          */}
          <a
            href={`/crm/companies/${match.id}`}
            target="_blank"
            rel="noreferrer"
            className="truncate text-[11px] text-content underline hover:text-brand-600"
          >
            {match.name}
          </a>
          <span className="truncate text-[11px] text-faint">
            {[
              match.branchCount && match.branchCount > 0
                ? `${match.branchCount} branches`
                : null,
              match.tradeAccountNumber,
              match.city,
              match.ownerName,
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </li>
      ))}
      {matches.length > 8 ? (
        <li className="py-0.5 text-[11px] text-faint">
          …and {matches.length - 8} more
        </li>
      ) : null}
    </ul>
  );
}
